import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { Dialog, PageHeader, Button, EditSvg, PlusSvg } from 'Components/ui';
import { getPolicyById, deletePolicy, getPolicyAcceptsSummary } from 'services/policies-service';
import { isPolicyVersionEditable } from 'utils/policy_version_utils';

/* ── Status colours ── */
const STATUS_STYLE = {
  'Publicado':  { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  'Aprovado':   { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  'Em revisão': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  'Revisado':   { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  'Arquivado':  { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
  'Iniciado':   { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
  'Concluído':  { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

/* ── Styled components ── */

const PageWrap = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

/* Info card */
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const InfoField = styled.div`
  ${p => p.$full && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 6px;
`;

const FieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${p => p.$muted ? 'var(--vo-ink-2)' : 'var(--vo-ink)'};
  margin: 0;
  line-height: ${p => p.$muted ? '1.65' : '1.4'};
`;

const ChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-brand);
  background: var(--vo-capri-50);
  padding: 3px 12px;
  border-radius: 20px;
  display: inline-block;
`;

/* Versions section */
const VersionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const VersionsTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const AddVersionBtn = styled.button`
  height: 40px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: var(--color-brand-strong); }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${p => p.$center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  background: var(--vo-mist);
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  padding: 13px 16px;
`;

const CellVersion = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink);
`;

const CellAuthor = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

const CellDate = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

const StatusBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
`;

const AcceptsNum = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-brand);
`;

const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

/* ── SVG Icons ── */
const IconEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconUsers = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
/* ── Component ── */

export const PoliciesShow = () => {
  const { t } = useTranslation(['policy']);
  const { id } = useParams();
  const navigate = useNavigate();

  const [deleteOpen, setDeleteOpen] = useState(false);

  const { isLoading, error, data: policy } = useQuery(
    ['getPolicyById', id],
    async () => {
      const res = await getPolicyById(id);
      return res.status === 200 ? res.data : null;
    }
  );

  // Policy versions carry no _id (none of 107 on staging, 2026-09-26): keying this off v._id gave
  // "" for a single-version policy, so the query never ran and the column stayed "—".
  const versionIds = (policy?.policyVersions || []).map((v, i) => `${i}:${v.version}`).join(',');

  const { data: acceptsByVersion } = useQuery(
    ['getPolicyVersionsAccepts', id, versionIds],
    async () => {
      const versionList = policy?.policyVersions || [];
      const entries = await Promise.all(
        versionList.map(async (v, idx) => {
          try {
            const res = await getPolicyAcceptsSummary(id, idx);
            return [idx, res.status === 200 ? res.data : null];
          } catch {
            return [idx, null];
          }
        })
      );
      return Object.fromEntries(entries);
    },
    { enabled: !!versionIds }
  );

  const handleDeletePolicy = async () => {
    try {
      await deletePolicy(id);
      navigate('/policies');
    } catch (err) {
      console.error('Erro ao excluir política:', err);
    }
  };

  if (isLoading) return <Container><PageLoaderShow /></Container>;
  if (error || !policy) return <Container><div>{t('policy:page.error')}</div></Container>;

  const versions = policy.policyVersions || [];
  const canDelete = !versions.some(v => v.publishedDate != null);

  return (
    <Container>
      <PageWrap>
        {/* Page Header */}
        <PageHeader
          title={policy.name}
          segments={[
            { label: t('common:breadcrumbs.compliance') },
            { label: t('policy:page.title'), route: '/policies' },
            { label: policy.name },
          ]}
          actions={
            <HeaderActions>
              {/* Delete sits on the LEFT, away from the primary slot: the create form's Save button
                  occupies the rightmost position, so a double-click on Save must never land on Delete. */}
              {canDelete && (
                <Button variant="danger" type="button" onClick={() => setDeleteOpen(true)} sx={{ height: '40px', padding: '0 16px', marginRight: '16px' }}>
                  {t('policy:show.deleteButton')}
                </Button>
              )}
              <Button variant="outline" type="button" onClick={() => navigate(`/policies/edit/${id}`)} startIcon={<EditSvg />} sx={{ height: '40px', padding: '0 16px' }}>
                {t('policy:show.editButton')}
              </Button>
            </HeaderActions>
          }
        />

        {/* Info Card */}
        <InfoCard>
          <InfoGrid>
            <InfoField>
              <FieldLabel>{t('policy:show.detail.classification')}</FieldLabel>
              <FieldValue>{policy.classification || '—'}</FieldValue>
            </InfoField>

            <InfoField>
              <FieldLabel>{t('policy:show.detail.userGroups')}</FieldLabel>
              {policy.userGroups?.filter(Boolean).length > 0 ? (
                <ChipWrap>
                  {policy.userGroups.filter(Boolean).map(g => (
                    <Chip key={g._id}>{g.name}</Chip>
                  ))}
                </ChipWrap>
              ) : (
                <FieldValue>—</FieldValue>
              )}
            </InfoField>

            <InfoField $full>
              <FieldLabel>{t('policy:show.detail.description')}</FieldLabel>
              <FieldValue $muted>{policy.description || '—'}</FieldValue>
            </InfoField>
          </InfoGrid>
        </InfoCard>

        {/* Versions Section */}
        <VersionsHeader>
          <VersionsTitle>{t('policy:show.versionsTitle')}</VersionsTitle>
          <AddVersionBtn
            type="button"
            onClick={() => navigate(`/policies/${id}/versions/add`)}
          >
            <PlusSvg />
            {t('policy:versions.titleAdd')}
          </AddVersionBtn>
        </VersionsHeader>

        <TableCard>
          <StyledTable>
            <thead>
              <tr>
                <Th>{t('policy:show.table.version')}</Th>
                <Th>{t('policy:show.table.author')}</Th>
                <Th>{t('policy:show.table.date')}</Th>
                <Th>{t('policy:show.table.status')}</Th>
                <Th>{t('policy:show.table.accepts')}</Th>
                <Th $center>{t('policy:show.table.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {versions.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '24px 16px', textAlign: 'center', fontFamily: 'Lato,sans-serif', fontSize: 13, color: 'var(--vo-slate)' }}>
                    {t('policy:versions.noData')}
                  </td>
                </tr>
              ) : (
                versions.map((ver, idx) => {
                  const statusStyle = STATUS_STYLE[ver.status] || { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
                  const authorName = ver.author?.name || ver.author?.email || '—';
                  const pubDate = formatDate(ver.publishedDate || ver.createdAt);
                  const accepts = acceptsByVersion?.[idx] ?? null;

                  const canEdit = isPolicyVersionEditable(ver);

                  return (
                    <Tr key={ver._id || idx}>
                      <Td><CellVersion>{ver.version || `v${idx + 1}`}</CellVersion></Td>
                      <Td><CellAuthor>{authorName}</CellAuthor></Td>
                      <Td><CellDate>{pubDate}</CellDate></Td>
                      <Td>
                        {ver.status
                          ? <StatusBadge $color={statusStyle.color} $bg={statusStyle.bg}>{ver.status}</StatusBadge>
                          : <CellDate>—</CellDate>
                        }
                      </Td>
                      <Td>
                        {accepts !== null
                          ? <AcceptsNum data-testid={`policy-version-accepts-${idx}`}>{accepts.sent} / {accepts.accepted}</AcceptsNum>
                          : <CellDate>—</CellDate>
                        }
                      </Td>
                      <Td>
                        <ActionBtns>
                          <Button
                            iconOnly
                            variant="icon"
                            type="button"
                            title={t('policy:show.viewVersion')}
                            onClick={() => navigate(`/policies/show/${id}/versions/${idx}`)}
                          >
                            <IconEye />
                          </Button>
                          {canEdit && (
                            <Button
                              iconOnly
                              variant="icon"
                              type="button"
                              title={t('policy:show.editVersion')}
                              onClick={() => navigate(`/policies/${id}/versions/edit/${idx}`)}
                            >
                              <EditSvg />
                            </Button>
                          )}
                          <Button
                            iconOnly
                            variant="icon"
                            type="button"
                            title={t('policy:show.viewAccepts')}
                            onClick={() => navigate(`/policies/show/${id}/versions/${idx}/accepts`)}
                          >
                            <IconUsers />
                          </Button>
                        </ActionBtns>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </tbody>
          </StyledTable>
        </TableCard>

        {/* Delete policy dialog */}
        <Dialog
          open={deleteOpen}
          onConfirm={handleDeletePolicy}
          onCancel={() => setDeleteOpen(false)}
          title={t('policy:show.deletePolicy.title')}
          message={t('policy:show.deletePolicy.confirm', { name: policy.name })}
        />

      </PageWrap>
    </Container>
  );
};
